import _ from "lodash";
import { Form } from "formik";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { admin_cse } from "@/config/roles";
import { adminRequests } from "@/api/(requests)/admin";
import Button from "@/components/UI/Button";
import { Button2 } from "@/ui2/atoms/Button";
import CardHeading from "@/components/UI/Headings/CardHeading";
import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { CustomForm } from "@/component-library/CustomForm";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { FormSelect } from "@/component-library/FormFields/FormSelect/FormSelect";
import { FormTextInput } from "@/component-library/FormFields/FormTextInput/FormTextInput";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { isAuthorized } from "@/utils/authToken";
import { localTimestampFromStandardTimestamp } from "@/utils/momentUtils";
import { toSentenceCase } from "@/utils/filters";
import { useProviderInactivationOptions } from "@/utils/hooks/useProviderInactivationOptions";

import {
  PROVIDER_STATUS_OPTIONS,
  PROVIDER_STATUS_DETAILS_SCHEMA,
  PROVIDER_INACTIVATION_REASON_OTHER_TEXT_ID,
  PROVIDER_INACTIVATION_REASON_OTHER_TEXT_CHARACTER_LIMIT,
} from "./ProviderStatusDetailsSchema";

import "./ProviderStatusDetails.styles.scss";

export function ProviderStatusDetails({ providerDetails, refetchProvider }) {
  const [editProviderStatus, setEditProviderStatus] = useState(false);
  const { selectableOptions: providerInactivationSelectableOptions } =
    useProviderInactivationOptions();
  const dispatch = useDispatch();

  const onSubmit = async (formData) => {
    const data = {
      provider_id: _.get(providerDetails, "username"),
      status: formData.status,
    };

    if (formData.status == "INACTIVE") {
      data.inactivation_reason_text_id = formData.inactivation_reason_text_id;
    }

    if (
      formData.status == "INACTIVE" &&
      formData.inactivation_reason_text_id === PROVIDER_INACTIVATION_REASON_OTHER_TEXT_ID
    ) {
      data.inactivation_reason_other_text = formData.inactivation_reason_other_text;
    }

    dispatch(showLoader());
    await adminRequests.change_provider_status({
      context: { showLoader: false },
      data,
    });
    await refetchProvider({
      context: { showLoader: false },
    });
    dispatch(hideLoader());

    setEditProviderStatus(false);
  };

  const renderStatusForm = (formikProps) => {
    return (
      <Form>
        <div className="mx-auto px-0 mb-3">
          <div className="ProviderStatusDetails--form_container">
            <FormSelect
              formikProps={formikProps}
              name="status"
              onChangeCallBack={async (_name, value) => {
                if (
                  // if the value we are on is active
                  // skip the callback
                  formikProps.values.status === "ACTIVE" ||
                  // if the value we are on is inactive
                  // and the new value is not active
                  // skip the callback
                  value !== "ACTIVE"
                ) {
                  return;
                }

                // reset fields
                await formikProps.setFieldValue("inactivation_reason_text_id", "");
                await formikProps.setFieldValue("inactivation_reason_other_text", "");
              }}
              options={PROVIDER_STATUS_OPTIONS}
              title="Provider Status"
              triggerErrorOnChange
            />

            {formikProps.values.status === "INACTIVE" && (
              <>
                <FormSelect
                  formikProps={formikProps}
                  name="inactivation_reason_text_id"
                  onChangeCallBack={async (_name, value) => {
                    if (
                      // if the value we are on is not the other text
                      // skip the callback
                      formikProps.values.inactivation_reason_text_id !==
                        PROVIDER_INACTIVATION_REASON_OTHER_TEXT_ID ||
                      // if the value we are on is the other text
                      // and the new value is the other text
                      // skip the callback
                      value === PROVIDER_INACTIVATION_REASON_OTHER_TEXT_ID
                    ) {
                      return;
                    }

                    // reset other text
                    await formikProps.setFieldValue("inactivation_reason_other_text", "");
                  }}
                  options={providerInactivationSelectableOptions}
                  title="Inactivation Reason"
                  triggerErrorOnChange
                />

                {formikProps.values?.inactivation_reason_text_id ==
                  PROVIDER_INACTIVATION_REASON_OTHER_TEXT_ID && (
                  <div className="ProviderStatusDetails--inactivation_reason_other_text_container">
                    <FormTextInput
                      formikProps={formikProps}
                      name="inactivation_reason_other_text"
                      title="Other (Specify...)"
                      triggerErrorOnChange
                    />
                    <CharacterCounter
                      characterLimit={PROVIDER_INACTIVATION_REASON_OTHER_TEXT_CHARACTER_LIMIT}
                      className="ProviderStatusDetails--inactivation_reason_other_character_counter"
                      length={formikProps.values.inactivation_reason_other_text.length}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="d-lg-flex d-block text-center justify-content-lg-end tw:justify-center">
          <Button
            className="Btn Btn--otl-pri Btn-sm mr-lg-3 mb-3 mb-lg-0"
            // clears the formik state by unmounting it
            onClick={() => setEditProviderStatus(!editProviderStatus)}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="Btn Btn--pri Btn-sm"
            // disable the submit button if all visible errors are not resolved
            disabled={Boolean(
              new Set(Object.keys(formikProps.errors)).intersection(
                new Set(Object.keys(formikProps.touched)),
              ).size,
            )}
            type="submit"
          >
            Save
          </Button>
        </div>
      </Form>
    );
  };

  if (!providerDetails) {
    return <></>;
  }

  return (
    <DashboardCard2
      Actions={
        isAuthorized(admin_cse) && (
          <Button2 onPress={() => setEditProviderStatus(!editProviderStatus)} variant="link">
            Edit
          </Button2>
        )
      }
      data-public="true"
      title="Status"
    >
      {isAuthorized(admin_cse) && <div className="ml-4 mt-2 mb-4"></div>}

      <div className="no-gutters mt-4 mb-3 col-12 px-3 px-lg-4">
        <p className="fs-14 ProviderStatusDetails--note_text">
          Setting a provider's status to inactive will sign the provider out from all devices,
          remove working hours and payers from their account, and cancel all appointments.
        </p>
      </div>

      {!editProviderStatus && (
        <div className="no-gutters my-4 col-12 px-3 px-lg-4 ProviderStatusDetails--read_only_container">
          <div className="col-12 col-lg-6">
            <CardHeading className="fs-14 mx-0" text="Provider Status" />
            <p className="fs-14 mx-0">{toSentenceCase(providerDetails.status)}</p>
          </div>

          {providerDetails.status === "INACTIVE" && (
            <div>
              <CardHeading className="fs-14 mx-0" text="Provider Inactivation Reason" />
              <p className="fs-14 mx-0 text-break">{providerDetails.inactivation_reason_text}</p>
            </div>
          )}

          {providerDetails.status_updated_by_name && providerDetails.status_update_timestamp && (
            <div className="ProviderStatusDetails--inactivation_meta_data_container">
              <p className="ProviderStatusDetails--inactivation_meta_data_text">
                Last Updated by {providerDetails.status_updated_by_name}
              </p>

              <p className="ProviderStatusDetails--inactivation_meta_data_text">
                {localTimestampFromStandardTimestamp(
                  providerDetails.status_update_timestamp,
                  "M/D/YYYY",
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {editProviderStatus && (
        <div className="no-gutters my-4 col-12 px-3 px-lg-4">
          <CustomForm
            initialValues={{
              inactivation_reason_other_text: providerDetails.inactivation_reason_other_text ?? "",
              inactivation_reason_text_id: providerDetails.inactivation_reason_text_id ?? "",
              status: providerDetails.status ?? "",
            }}
            onSubmit={onSubmit}
            render={renderStatusForm}
            validationSchema={PROVIDER_STATUS_DETAILS_SCHEMA}
          />
        </div>
      )}
    </DashboardCard2>
  );
}
